import { LiveAnnouncer } from '@angular/cdk/a11y';
import { ComponentType, Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, PortalInjector } from '@angular/cdk/portal';
import {
    EmbeddedViewRef,
    Inject,
    Injectable,
    InjectionToken,
    Injector,
    OnDestroy,
    TemplateRef
} from '@angular/core';
import { FINRA_TOAST_DATA, ToastConfig } from './toast-config';
import { ToastContainerComponent } from './toast-container/toast-container.component';
import { ToastRef } from './toast-ref';

/** Injection token that can be used to specify default toast. */
export const FINRA_TOAST_DEFAULT_OPTIONS =
    new InjectionToken<ToastConfig>('finra-toast-default-options', {
        providedIn: 'root',
        factory: TOAST_DEFAULT_OPTIONS_FACTORY,
    });

/** @docs-private */
export function TOAST_DEFAULT_OPTIONS_FACTORY(): ToastConfig {
    return new ToastConfig();
}

@Injectable()
export class ToastService implements OnDestroy {

    /**
     * Reference to the current toast in the view *at this level* (in the Angular injector tree).
     * If there is a parent toast service, all operations should delegate to that parent
     * via `openedToastRef`.
     */
    private toastRefAtThisLevel: ToastRef<any> | null = null;

    /** Reference to the currently opened toast at *any* level. */
    get openedToastRef(): ToastRef<any> | null {
        // const parent = this.parentToast;
        // return parent ? parent.openedToastRef : this.toastRefAtThisLevel;
        return this.toastRefAtThisLevel;
    }

    set openedToastRef(value: ToastRef<any> | null) {
        // if (this.parentToast) {
        //     this.parentToast.openedToastRef = value;
        // } else {
        this.toastRefAtThisLevel = value;
        // }
    }

    constructor(
        private overlay: Overlay,
        private live: LiveAnnouncer,
        private injector: Injector,
        // @Optional() @SkipSelf() private parentToast: ToastService,
        @Inject(FINRA_TOAST_DEFAULT_OPTIONS) private defaultConfig: ToastConfig) {
    }

    public open(template: TemplateRef<any>) {
        this.attach(template);
    }

    public openFromComponent<T>(component: ComponentType<T>, config?: ToastConfig) {
        this.attach(component, config);

    }

    /**
     * Dismisses the currently-visible toast.
     */
    dismiss(): void {
        if (this.openedToastRef) {
            this.openedToastRef.dismiss();
        }
    }

    ngOnDestroy() {
        // Only dismiss the toast at the current level on destroy.
        if (this.toastRefAtThisLevel) {
            this.toastRefAtThisLevel.dismiss();
        }
    }

    /**
     * Attaches the toast container component to the overlay.
     */
    private attachToastContainer(overlayRef: OverlayRef, config: ToastConfig) {

        const injector = new PortalInjector(this.injector, new WeakMap([[ToastConfig, config]]));

        // const portalOutlet = new DomPortalOutlet(window.document.body, this.cfr, this.applicationRef, this.injector);
        const containerPortal = new ComponentPortal(ToastContainerComponent, config.viewContainerRef, injector);
        const containerRef = overlayRef.attach(containerPortal);
        containerRef.instance.toastConfig = config;

        return containerRef.instance;
    }

    /**
     * Places a new component or a template as the content of the toast container.
     */
    private attach<T>(content: ComponentType<T> | TemplateRef<T>, userConfig?: ToastConfig): ToastRef<T | EmbeddedViewRef<any>> {

        const config = { ...new ToastConfig(), ...this.defaultConfig, ...userConfig };
        const overlayRef = this.createOverlay(config);
        const container = this.attachToastContainer(overlayRef, config);
        const toastRef = new ToastRef<T | EmbeddedViewRef<any>>(container, overlayRef);

        if (content instanceof TemplateRef) {
            // const portal = new TemplatePortal(content, null!, {
            //     $implicit: config.data,
            //     ToastRef
            // } as any);

            // containerRef.attachTemplatePortal(portal);
        } else {
            const injector = this.createInjector(config, toastRef);
            const portal = new ComponentPortal(content, null, injector);
            const contentRef = container.attachComponentPortal(portal);

            toastRef.instance = contentRef.instance;
        }

        this.animateToast(toastRef, config);
        this.openedToastRef = toastRef;
        return this.openedToastRef;
    }

    /** Animates the old toast out and the new one in. */
    private animateToast(toastRef: ToastRef<any>, config: ToastConfig) {
        // When the toast is dismissed, clear the reference to it.
        toastRef.afterDismissed().subscribe(() => {
            // Clear the toast ref if it hasn't already been replaced by a newer toast.
            if (this.openedToastRef === toastRef) {
                this.openedToastRef = null;
            }

            if (config.announcementMessage) {
                this.live.clear();
            }
        });

        if (this.openedToastRef) {
            // If a toast is already in view, dismiss it and enter the
            // new toast after exit animation is complete.
            this.openedToastRef.afterDismissed().subscribe(() => {
                toastRef.containerInstance.enter();
            });
            this.openedToastRef.dismiss();
        } else {
            // If no toast is in view, enter the new toast.
            toastRef.containerInstance.enter();
        }

        // If a dismiss timeout is provided, set up dismiss based on after the toast is opened.
        if (config.duration && config.duration > 0) {
            toastRef.afterOpened().subscribe(() => toastRef.dismissAfter(config.duration));
        }

        if (config.announcementMessage) {
            this.live.announce(config.announcementMessage, config.politeness);
        }
    }

    /**
     * Creates a new overlay and places it in the correct location.
     * @param config The user-specified toast config.
     */
    private createOverlay(config: ToastConfig): OverlayRef {
        const overlayConfig = new OverlayConfig();
        overlayConfig.direction = config.direction;

        const positionStrategy = this.overlay.position().global();

        // Set horizontal position
        const isRtl = config.direction === 'rtl';
        const isLeft = (
            config.horizontalPosition === 'left' ||
            (config.horizontalPosition === 'start' && !isRtl) ||
            (config.horizontalPosition === 'end' && isRtl));
        const isRight = !isLeft && config.horizontalPosition !== 'center';
        if (isLeft) {
            positionStrategy.left('0');
        } else if (isRight) {
            positionStrategy.right('0');
        } else {
            positionStrategy.centerHorizontally();
        }
        // Set vertical position
        if (config.verticalPosition === 'bottom') {
            positionStrategy.bottom('0');
        } else {
            positionStrategy.top('0');
        }

        overlayConfig.positionStrategy = positionStrategy;
        return this.overlay.create(overlayConfig);
    }

    /**
     * Creates an injector to be used inside of a toast component.
     * @param config Config that was used to create the toast.
     * @param snackBarRef Reference to the toast.
     */
    private createInjector<T>(
        config: ToastConfig,
        toastRef: ToastRef<T>): PortalInjector {

        return new PortalInjector(this.injector, new WeakMap<any, any>([
            [ToastRef, toastRef],
            [FINRA_TOAST_DATA, config.data]
        ]));
    }
}
